<template>
    <div v-if="hide || active || formStepsEnabled" v-show="active"
        :id="group + '_' + name"
        :class="[{ active }, 'tab-bar-content', 'tab-bar-content-' + group]"
        :aria-labelledby="group + '_' + name + '_label'"
        role="tabpanel"
        tabindex="0"
        ref="elementRef">
        <slot v-if="hide || active || (formStepsEnabled && !stepAsComponent)"/>
    </div>
</template>

<script lang="ts" setup>
    import {usePortuna} from '@/composables/portuna';
    import {FormStepsContentFieldRegistryKey, FormStepsKey} from '@/library/form';
    import {computed, getCurrentInstance, inject, onMounted, onUnmounted, provide, ref, type ShallowRef, shallowRef, triggerRef} from 'vue';
    import {
        getVueChildren,
        identifyElement,
    } from '@/library/utils';
    import {useStore} from 'vuex';

    const portuna = usePortuna();
    const store   = useStore();

    const props = withDefaults(defineProps<{
        name: string;
        group: string;
        hide?: boolean;
        forceShow?: boolean;
        stepIndex?: number;
    }>(), {
        hide: false,
        forceShow: false,
    });

    const elementRef                                             = ref<HTMLElement | null>(null);
    const controllers: ShallowRef<FormStepsFieldControllerRaw[]> = shallowRef([]);
    const formComponent                                          = ref<any>(null);

    const instanceIndex = ref<number>(0);

    const ctx = inject<Nullable<IFormStepsInternalContext>>(FormStepsKey, null);

    const options = computed<TabBarData>(() => store.state.tabBar[props.group] || {});

    const active = computed(() => props.forceShow || (options.value && options.value.active === props.name));

    const formStepsEnabled = !!ctx;
    const stepAsComponent  = ctx?.stepAsComponent.value ?? false;

    const instance = getCurrentInstance();

    async function getFirstFormComponent()
    {
        if (formComponent.value)
        {
            return formComponent.value;
        }

        const selfComponent = instance?.proxy as any;

        const children = getVueChildren(identifyElement(selfComponent));

        return children[0] || null;
    }

    const exposed = Object.assign({}, portuna, {
        componentName: 'TabBarContent',
        el: elementRef,
        controllers,
        formComponent,
        getFirstFormComponent,
    }) as IFormStepContentContext;

    onMounted(() =>
    {
        if (!ctx)
        {
            return;
        }

        if (typeof props.stepIndex !== 'undefined' && !isNaN(props.stepIndex))
        {
            instanceIndex.value = Number(props.stepIndex);
        }
        else
        {
            while (ctx.getContent(instanceIndex.value))
            {
                instanceIndex.value++;
            }
        }

        ctx.registerContent(
            instanceIndex.value,
            exposed,
        );
    });

    onUnmounted(() =>
    {
        if (!ctx)
        {
            return;
        }

        ctx.unregisterContent(instanceIndex.value);
    });

    const controllerFunctions = {
        add(controller: FormStepsFieldControllerRaw)
        {
            if (!controllers.value.includes(controller))
            {
                controllers.value.push(controller);
            }

            triggerRef(controllers);
        },
        remove(controller: FormStepsFieldControllerRaw)
        {
            controllers.value = controllers.value.filter(existing => existing !== controller);

            triggerRef(controllers);
        },
    };

    provide(FormStepsContentFieldRegistryKey, controllerFunctions);

    defineExpose(exposed);
</script>